import { type ReactNode, useEffect, useMemo } from "react";

import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useDebounce } from "hooks";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import { selectCurrentClinic } from "features/auth/auth-slice";
import {
  AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED,
  AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED,
  AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED
} from "features/medical-scribe/medical-scribe-constants";
import { getMedicalScribeText } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import { ReportTableCell } from "features/reports/components/report-table-cell/report-table-cell.component";
import {
  DEFAULT_AMBIENT_SCRIBES_QUERY_DETAILED,
  useGetAmbientScribesQuery
} from "services/ambient-scribe";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { convertHtmlToText } from "utils/common";

import ScribeListRow from "./scribe-list-row.component";
import { getWordsAround } from "./utils";

type SelectMedicalScribeFieldProps = {
  patientId: number;
  search: string;
  selected?: AmbientScribe | null;
  onSelect: (template: AmbientScribe | null) => void;
  /** Refreshes selection from list data without a new user pick (avoids resetting edit mode). */
  onSelectedScribeSync?: (scribe: AmbientScribe | null) => void;
  onEdit?: (scribe: AmbientScribe) => void;
  setSearch: (value: string) => void;
  extraStatuses?: number[];
  /** Replaces the "none found" line when the patient has no recordings at all, never after a search. */
  emptyState?: ReactNode;
  /** Whether the patient has any recordings, ignoring the search, each time a read settles. */
  onHasRecordingsChange?: (hasRecordings: boolean) => void;
};
export default function SelectMedicalScribeField({
  search,
  patientId,
  selected,
  onSelect,
  onSelectedScribeSync,
  onEdit,
  setSearch,
  extraStatuses,
  emptyState,
  onHasRecordingsChange
}: SelectMedicalScribeFieldProps) {
  const currentClinic = useAppSelector(selectCurrentClinic);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "mobile")); // Between 768px and 1320px
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const { value: dSearch } = useDebounce(search, 250);

  const baseStatuses = [
    AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED,
    AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED,
    AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED
  ];
  const statusFilter = extraStatuses?.length
    ? [...baseStatuses, ...extraStatuses]
    : baseStatuses;

  const {
    data: scribes = [],
    isLoading,
    isFetching
  } = useGetAmbientScribesQuery(
    {
      query: DEFAULT_AMBIENT_SCRIBES_QUERY_DETAILED,
      patient: patientId,
      status: statusFilter
    },
    {
      skip: !currentClinic?.id,
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (isLoading || isFetching) return;
    onHasRecordingsChange?.(scribes.length > 0);
  }, [isLoading, isFetching, scribes.length, onHasRecordingsChange]);

  const filteredScribes = useMemo(() => {
    if (dSearch) {
      const lowerSearch = dSearch.toLowerCase();

      return scribes
        .filter((scribe) =>
          [scribe?.summary, scribe?.soap_notes, getMedicalScribeText(scribe)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            ?.includes(lowerSearch)
        )
        .map((scribe) => {
          const text = [
            convertHtmlToText(scribe?.summary),
            convertHtmlToText(scribe?.soap_notes),
            getMedicalScribeText(scribe)
          ]
            .filter(Boolean)
            .join(" ");

          const words = getWordsAround(
            text,
            text.toLowerCase().indexOf(lowerSearch),
            7,
            7
          );

          return {
            ...scribe,
            preview: [words.before, words.after].filter(Boolean).join(" ")
          };
        });
    }
    return scribes;
  }, [dSearch, scribes]);

  useEffect(() => {
    if (!selected?.id) return;
    const scribe = filteredScribes.find((s) => s.id === selected.id);
    const syncSelection = onSelectedScribeSync ?? onSelect;
    syncSelection(scribe ?? null);
  }, [selected?.id, filteredScribes, onSelect, onSelectedScribeSync]);

  return (
    <Stack
      sx={{
        width: isMobile ? "100%" : isTablet ? "100%" : 600,
        maxWidth: "100%",
        minWidth: isTablet ? "280px" : "auto",
        height: isTablet ? "100%" : "auto",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <TextField
        autoFocus
        value={search}
        fullWidth
        placeholder="Search Medical Scribes"
        sx={{
          mb: isTablet ? 2 : 3,
          position: "sticky",
          top: 0,
          "& .MuiInputBase-input": {
            fontSize: isTablet ? "16px" : "14px" // Prevent zoom on iOS
          }
        }}
        onChange={(event) => setSearch(event.target.value)}
      />
      <TableContainer
        sx={{
          overflow: "auto",
          height: isTablet ? "30vh" : "auto",
          flex: isTablet ? 1 : "none"
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <ReportTableCell sx={{ fontSize: isTablet ? "12px" : "14px" }}>
                {isTablet ? "Date" : "Date Created"}
              </ReportTableCell>
              <ReportTableCell sx={{ fontSize: isTablet ? "12px" : "14px" }}>
                Duration
              </ReportTableCell>
              <ReportTableCell></ReportTableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              cursor: "pointer"
            }}
          >
            {!(isLoading || isFetching) &&
              Boolean(filteredScribes?.length) &&
              filteredScribes?.map((row, index) => (
                <ScribeListRow
                  key={index}
                  row={row}
                  dSearch={dSearch}
                  selected={selected}
                  onSelect={onSelect}
                  onEdit={onEdit}
                />
              ))}
            {(isLoading || isFetching) && <CircularProgress />}
            {!(isLoading || isFetching) &&
              filteredScribes?.length === 0 &&
              (emptyState && !dSearch ? (
                <TableRow>
                  <ReportTableCell colSpan={2}>{emptyState}</ReportTableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <ReportTableCell data-testid="row-no-data-found" colSpan={2}>
                    No Medical Scribes were found
                  </ReportTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
